/**
 * FibiWidget.jsx · v101.24.0 · Botón flotante de «Fibi», asistente virtual (Ayuda IA).
 * Aparece abajo a la derecha en todas las pantallas con sesión iniciada.
 * Muestra el globo «¡Hola! Soy Fibi, tu asistente virtual. ¡Pregúntame!» y abre el chat.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import FibiChat from "./FibiChat";
import RobotMascot from "./RobotMascot";
import { X, Maximize2 } from "lucide-react";

const BUBBLE_KEY = "afc_fibi_bubble_seen";

export default function FibiWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [bubble, setBubble] = useState(false);

  // Globo de bienvenida: una vez por sesión, durante unos segundos
  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem(BUBBLE_KEY) === "1"; } catch { /* ignore */ }
    if (seen) return undefined;
    const t1 = setTimeout(() => setBubble(true), 1200);
    const t2 = setTimeout(() => {
      setBubble(false);
      try { sessionStorage.setItem(BUBBLE_KEY, "1"); } catch { /* ignore */ }
    }, 10000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (!user || location.pathname.startsWith("/ayuda")) return null;

  const toggle = () => {
    setOpen((v) => !v);
    setBubble(false);
    try { sessionStorage.setItem(BUBBLE_KEY, "1"); } catch { /* ignore */ }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3 pointer-events-none" data-testid="fibi-widget">
      {open && (
        <div className="pointer-events-auto w-[min(400px,calc(100vw-2rem))] h-[min(600px,calc(100vh-7rem))] rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col bg-white" data-testid="fibi-panel">
          <div className="flex items-center gap-2 px-3 py-2 bg-[#0A3747] text-white">
            <div className="w-9 h-9 rounded-full bg-[#0E445A] flex items-center justify-center overflow-hidden">
              <RobotMascot size={36} forceDark />
            </div>
            <div className="flex-1 leading-tight">
              <div className="font-semibold text-sm">Fibi</div>
              <div className="text-[11px] text-[#E0E621]">Tu asistente virtual · Ayuda IA</div>
            </div>
            <button onClick={() => { setOpen(false); navigate("/ayuda"); }} title="Abrir en pantalla completa" className="p-1.5 rounded hover:bg-white/10">
              <Maximize2 size={15} />
            </button>
            <button onClick={() => setOpen(false)} title="Cerrar" className="p-1.5 rounded hover:bg-white/10" data-testid="fibi-close">
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 min-h-0">
            <FibiChat compact />
          </div>
        </div>
      )}

      <div className="flex items-end gap-2">
        {!open && bubble && (
          <div className="pointer-events-auto relative max-w-[230px] rounded-2xl rounded-br-sm bg-white text-gray-800 shadow-xl border border-gray-200 px-3 py-2 text-sm" data-testid="fibi-bubble">
            <button onClick={() => setBubble(false)} className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center" title="Ocultar">
              <X size={11} />
            </button>
            <button onClick={toggle} className="text-left">
              ¡Hola! Soy <b>Fibi</b>, tu asistente virtual. <span className="text-[#0A3747] font-semibold">¡Pregúntame!</span>
            </button>
          </div>
        )}
        <button
          onClick={toggle}
          onMouseEnter={() => !open && setBubble(true)}
          className="pointer-events-auto group relative w-16 h-16 rounded-full bg-[#0A3747] shadow-2xl ring-4 ring-[#E0E621]/60 hover:ring-[#E0E621] transition flex items-center justify-center overflow-hidden"
          title="Soy Fibi, tu asistente virtual. ¡Pregúntame!"
          aria-label="Abrir Fibi, asistente virtual"
          data-testid="fibi-button"
        >
          {open ? <X size={26} className="text-[#E0E621]" /> : <RobotMascot size={60} forceDark />}
          {!open && <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-[#E0E621] border-2 border-[#0A3747] animate-pulse" />}
        </button>
      </div>
    </div>
  );
}
